'use client';

import { isChunkLoadError, recoverFromChunkLoadError } from '@/utils/chunkLoadErrorGuard';
import { HStack, Text, UXButton, VStack } from '@particle-network/ui-react';
import * as Sentry from '@sentry/nextjs';
import { useEffect, useState } from 'react';

export default function Error({ error, reset }: { error: Error & { digest?: string }; reset: () => void }) {
  // A stale page that loaded a chunk from a newer deployment cannot be fixed by
  // reset() — only a full reload restores a consistent module graph.
  const isChunkError = isChunkLoadError(error);
  const [reloading, setReloading] = useState(false);

  useEffect(() => {
    if (isChunkError) {
      // The guard reports to Sentry itself (tagged), then reloads once; within
      // the cooldown it returns false so the error surfaces instead of looping.
      if (recoverFromChunkLoadError(error, 'error-boundary')) {
        setReloading(true);
      }
      return;
    }
    Sentry.captureException(error);
  }, [error, isChunkError]);

  if (reloading) {
    return (
      <VStack fullWidth fullHeight justify='center' items='center'>
        <Text body2 color='secondary' data-testid='error-reloading'>
          A new version is available. Reloading…
        </Text>
      </VStack>
    );
  }

  return (
    <VStack fullWidth fullHeight justify='center' items='center'>
      <VStack className='bg-content1 rounded-medium shadow-medium w-[480px] max-w-full p-6' gap={2}>
        <Text variant='h2' color='danger'>
          Oops! Something went wrong.
        </Text>

        <Text body2 color='warning' mt={2} className='max-h-[50vh] overflow-y-auto whitespace-pre-wrap'>
          {error?.message || 'An unexpected error occurred.'}
        </Text>

        <HStack fullWidth gap={4} mt={2}>
          <UXButton
            color='secondary'
            className='flex-1'
            size='lg'
            onPress={() => {
              window.open('https://t.me/universalx_support_bot', '_blank');
            }}
            data-testid='error-btn-1'
          >
            User Support
          </UXButton>

          <UXButton
            color='primary'
            onPress={isChunkError ? () => window.location.reload() : reset}
            className='flex-1'
            size='lg'
            data-testid='error-btn-2'
          >
            {isChunkError ? 'Refresh' : 'Try Again'}
          </UXButton>
        </HStack>

        <Text body2 color='tertiary'>
          {isChunkError
            ? '· A new version of the app is available. Refresh the page to continue.'
            : '· Please try again first. If the issue persists, refresh the page.'}
        </Text>
      </VStack>
    </VStack>
  );
}
